import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import {
  makeStyles,
  Popover,
  IconButton,
  Badge,
  Typography,
  Button,
  Box,
  LinearProgress,
  List,
  ListItem
} from "@material-ui/core";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import { i18n } from "../../translate/i18n";

const SirenIcon = ({ size = 20, className }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    className={className}
    style={{ display: "block" }}
  >
    <defs>
      <linearGradient id="sirenDomeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#ff7043" />
        <stop offset="50%" stopColor="#f44336" />
        <stop offset="100%" stopColor="#d32f2f" />
      </linearGradient>
      <linearGradient id="sirenRaysGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#ffca28" />
        <stop offset="100%" stopColor="#ff9800" />
      </linearGradient>
    </defs>
    {/* Base metálica */}
    <path
      d="M3 20h18v2H3z"
      fill="#78909c"
    />
    {/* Domo principal de la sirena con degradado cálido */}
    <path
      d="M4 20V14C4 9.58 7.58 6 12 6s8 3.58 8 8v6H4z"
      fill="url(#sirenDomeGrad)"
    />
    {/* Reflejo interior de lente con tinte ámbar suave */}
    <path
      d="M6 14H8c0-2.21 1.79-4 4-4V8c-3.31 0-6 2.69-6 6z"
      fill="#ffe082"
      opacity="0.85"
    />
    {/* Rayo central superior */}
    <path
      d="M11 2h2v3h-2z"
      fill="url(#sirenRaysGrad)"
    />
    {/* Rayo superior derecho */}
    <path
      d="M19.78 4.81l1.41 1.41-2.12 2.12-1.41-1.41z"
      fill="url(#sirenRaysGrad)"
    />
    {/* Rayo superior izquierdo */}
    <path
      d="M2.81 6.22l1.41-1.41 2.12 2.12-1.41 1.41z"
      fill="url(#sirenRaysGrad)"
    />
  </svg>
);

const useStyles = makeStyles((theme) => ({
  sirenButton: {
    padding: 6,
    marginRight: 6,
    position: "relative",
    color: "inherit",
    "&:hover": {
      backgroundColor: "rgba(255, 112, 67, 0.12)",
    },
  },
  sirenWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    animation: "$sirenPulse 1.6s infinite ease-in-out",
  },
  "@keyframes sirenPulse": {
    "0%": {
      transform: "scale(1)",
      filter: "drop-shadow(0 0 1px rgba(255, 112, 67, 0.4))",
    },
    "50%": {
      transform: "scale(1.06)",
      filter: "drop-shadow(0 0 3px rgba(255, 87, 34, 0.75))",
    },
    "100%": {
      transform: "scale(1)",
      filter: "drop-shadow(0 0 1px rgba(255, 112, 67, 0.4))",
    },
  },
  badge: {
    "& .MuiBadge-badge": {
      backgroundColor: "#ff5722",
      color: "#ffffff",
      fontWeight: 700,
      fontSize: "0.72rem",
      height: 18,
      minWidth: 18,
      padding: "0 4px",
      boxShadow: "0 0 3px rgba(0,0,0,0.3)",
    },
  },
  popoverPaper: {
    width: 370,
    maxWidth: "92vw",
    padding: theme.spacing(2),
    borderRadius: 12,
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.25)",
    border: "1px solid rgba(255, 112, 67, 0.3)",
  },
  headerBox: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  headerIcon: {
    marginRight: theme.spacing(1),
    display: "flex",
    alignItems: "center",
  },
  title: {
    fontWeight: 700,
    fontSize: "1.02rem",
    color: theme.palette.text.primary,
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: "0.82rem",
    marginBottom: theme.spacing(1.5),
  },
  list: {
    maxHeight: 280,
    overflowY: "auto",
    padding: 0,
    marginBottom: theme.spacing(1.5),
  },
  listItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255, 87, 34, 0.08)"
        : "#fff8f5",
    borderLeft: "4px solid #ff5722",
    borderRadius: 6,
    padding: theme.spacing(1, 1.5),
    marginBottom: theme.spacing(1),
  },
  limitItemHeader: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    marginBottom: 4,
  },
  limitName: {
    fontWeight: 600,
    fontSize: "0.84rem",
    color: theme.palette.text.primary,
  },
  limitBadge: {
    fontSize: "0.75rem",
    fontWeight: 700,
    color: "#e64a19",
    whiteSpace: "nowrap",
  },
  progressBar: {
    width: "100%",
    height: 6,
    borderRadius: 3,
    backgroundColor: "rgba(255, 87, 34, 0.2)",
    "& .MuiLinearProgress-bar": {
      backgroundColor: "#ff5722",
    },
  },
  actionsBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: theme.spacing(1),
    gap: theme.spacing(1),
  },
  btnDismiss: {
    textTransform: "none",
    fontWeight: 600,
  },
  btnUpgrade: {
    textTransform: "none",
    fontWeight: 600,
    backgroundColor: "#ff9800",
    color: "#ffffff",
    "&:hover": {
      backgroundColor: "#f57c00",
    },
  },
}));

const PlanLimitsSiren = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const socket = useContext(SocketContext);

  const [anchorEl, setAnchorEl] = useState(null);
  const [limitsData, setLimitsData] = useState([]);
  const [unseenReachedLimits, setUnseenReachedLimits] = useState([]);
  const isFetchingRef = useRef(false);

  const storageKey = user?.companyId
    ? `botmix_dismissed_limits_${user.companyId}`
    : "botmix_dismissed_limits";

  const fetchPlanUsage = useCallback(async () => {
    if (!user?.companyId || isFetchingRef.current) return;
    try {
      isFetchingRef.current = true;
      const { data } = await api.get("/dashboard/plan-usage");
      if (data && Array.isArray(data.limits)) {
        setLimitsData(data.limits);

        // Identificar límites alcanzados actualmente
        const currentReached = data.limits.filter((l) => l.isReached);

        // Obtener historial de descartados
        let dismissedKeys = [];
        try {
          const raw = localStorage.getItem(storageKey);
          if (raw) dismissedKeys = JSON.parse(raw);
          if (!Array.isArray(dismissedKeys)) dismissedKeys = [];
        } catch {
          dismissedKeys = [];
        }

        // Si algún límite descartado anteriormente ya bajó/se liberó, removerlo de descartados
        // para que si en el futuro vuelve a subir, vuelva a alertar
        const updatedDismissed = dismissedKeys.filter((key) =>
          currentReached.some((l) => l.key === key)
        );
        if (updatedDismissed.length !== dismissedKeys.length) {
          localStorage.setItem(storageKey, JSON.stringify(updatedDismissed));
          dismissedKeys = updatedDismissed;
        }

        // Filtrar límites alcanzados que NO han sido leídos/descartados
        const unseen = currentReached.filter(
          (l) => !dismissedKeys.includes(l.key)
        );

        setUnseenReachedLimits(unseen);
      }
    } catch (err) {
      console.error("[PlanLimitsSiren] Error al consultar límites del plan:", err);
    } finally {
      isFetchingRef.current = false;
    }
  }, [user?.companyId, storageKey]);

  useEffect(() => {
    fetchPlanUsage();

    // Consulta periódica cada 60 segundos
    const interval = setInterval(fetchPlanUsage, 60000);
    return () => clearInterval(interval);
  }, [fetchPlanUsage]);

  // Escuchar eventos de socket que alteren consumos o conexiones
  useEffect(() => {
    if (!socket || !user?.companyId) return;

    const onConsumptionUpdate = () => {
      fetchPlanUsage();
    };

    const companyId = user.companyId;
    socket.on(`company-${companyId}-ticket`, onConsumptionUpdate);
    socket.on(`company-${companyId}-whatsapp`, onConsumptionUpdate);

    return () => {
      socket.off(`company-${companyId}-ticket`, onConsumptionUpdate);
      socket.off(`company-${companyId}-whatsapp`, onConsumptionUpdate);
    };
  }, [socket, user?.companyId, fetchPlanUsage]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDismissAll = () => {
    try {
      // Guardar todos los límites actualmente alcanzados como leídos
      const currentReachedKeys = limitsData
        .filter((l) => l.isReached)
        .map((l) => l.key);

      localStorage.setItem(storageKey, JSON.stringify(currentReachedKeys));
      setUnseenReachedLimits([]);
      handleClose();
    } catch (err) {
      console.error("[PlanLimitsSiren] Error al descartar alertas:", err);
      handleClose();
    }
  };

  const handleGoToFinance = () => {
    handleClose();
    history.push("/financeiro");
  };

  const getFeatureName = (item) => {
    const key = `dashboard.planLimitsSiren.features.${item.key}`;
    const translated = i18n.t(key);
    return translated && translated !== key ? translated : item.name;
  };

  // Si no hay límites alcanzados sin leer, la sirena desaparece de la TopBar
  if (unseenReachedLimits.length === 0) {
    return null;
  }

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        onClick={handleOpen}
        className={classes.sirenButton}
        title={i18n.t("dashboard.planLimitsSiren.tooltip")}
        aria-label={i18n.t("dashboard.planLimitsSiren.ariaLabel")}
      >
        <Badge
          badgeContent={unseenReachedLimits.length}
          className={classes.badge}
        >
          <div className={classes.sirenWrapper}>
            <SirenIcon size={20} />
          </div>
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        classes={{ paper: classes.popoverPaper }}
      >
        <Box className={classes.headerBox}>
          <span className={classes.headerIcon}>
            <SirenIcon size={24} />
          </span>
          <Typography variant="h6" className={classes.title}>
            {i18n.t("dashboard.planLimitsSiren.title")}
          </Typography>
        </Box>

        <Typography variant="body2" className={classes.subtitle}>
          {i18n.t("dashboard.planLimitsSiren.subtitle")}
        </Typography>

        <List className={classes.list}>
          {unseenReachedLimits.map((item) => (
            <ListItem key={item.key} disableGutters className={classes.listItem}>
              <Box className={classes.limitItemHeader}>
                <Typography className={classes.limitName}>
                  {getFeatureName(item)}
                </Typography>
                <Typography className={classes.limitBadge}>
                  {item.used} / {item.max}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={100}
                className={classes.progressBar}
              />
            </ListItem>
          ))}
        </List>

        <Box className={classes.actionsBox}>
          <Button
            size="small"
            color="primary"
            variant="outlined"
            startIcon={<CheckCircleOutlineIcon />}
            onClick={handleDismissAll}
            className={classes.btnDismiss}
          >
            {i18n.t("dashboard.planLimitsSiren.btnDismiss")}
          </Button>

          <Button
            size="small"
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={handleGoToFinance}
            className={classes.btnUpgrade}
          >
            {i18n.t("dashboard.planLimitsSiren.btnUpgrade")}
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default PlanLimitsSiren;
